import Styles from './styles.module.css';
import React, { useEffect, useRef, useState } from 'react';
import {
  addDoc, collection, doc, getFirestore, increment, onSnapshot, orderBy, query, updateDoc,
} from 'firebase/firestore';
import type { ChatRoom } from '../../6.manage/index';
import { useAppSelector } from 'src/salon/app/hook';
import { studentAvatarUrl } from '../types';
import DefaultImg from '../../../../image/default/account.png';
import VisitRequestCard, { EventRequestCard, EventRequestMessage, parseEventRequestMessage, parseVisitRequestMessage, VisitRequestMessage } from './VisitRequestCard';

// ステータス変更時のテンプレート(StudentDetailPanel が応募設定から作る)。id は入れ直しの判定用。
export type MessageDraft = { text: string; id: number };

type Props = {
  chatRoom: ChatRoom;
  draft?: MessageDraft | null;
  // テンプレートを送信した・取り消したとき(パネル側のテンプレートを消す)
  onDraftUsed?: () => void;
};

type MessageItem = {
  id: string;
  send: string;
  text: string;
  time: Date;
  // サロン見学の申込カード(type: 'visit')の場合だけ入る
  visit: VisitRequestMessage | null;
  // 会社説明会・クーポンの申込カード(type: 'infoSession' | 'coupon')の場合だけ入る
  event: EventRequestMessage | null;
};

function toDate(value: any): Date {
  if (value?.toDate) return value.toDate();
  if (value) return new Date(value);
  return new Date();
}

// メッセージスレッド。応募・選考(6.manage)のContactMessageは自前の枠付きボックス+
// 履歴書/保存アイコン付きヘッダーを持つ独立コンポーネントで、そのまま埋め込むと
// パネルの枠の中にさらに枠がある見た目になってしまうため、このタブ専用に直書きする。
// 履歴書/保存はそれぞれ別タブ・タブ列末尾のアイコンとして既に用意されているため、
// ここでは会話の閲覧・送信だけに専念する。
const MessageTab: React.FC<Props> = ({ chatRoom, draft, onDraftUsed }) => {

  const company = useAppSelector((state) => state.company.company);
  const [ messages, setMessages ] = useState<MessageItem[]>([]);
  const [ sendText, setSendText ] = useState('');
  // 会社側の未読数(chats.company.badge)。0なら既読。ボタンの表示を切り替えるため購読する。
  const [ unread, setUnread ] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const avatarUrl = studentAvatarUrl(chatRoom);
  // 入力欄にステータス変更時のテンプレートが入っている間だけ出す案内
  const [ draftNotice, setDraftNotice ] = useState(false);
  // 送信中(ダブルクリックで同じメッセージが2件送られないよう、終わるまで次の送信を受け付けない)
  const sendingRef = useRef(false);
  const [ sending, setSending ] = useState(false);

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }

  // ステータスを変えたときのテンプレートを入力欄に入れる。入力中の文章があれば置き換えてよいか確認する。
  // パネル側のテンプレートは送信するまで残すため、他のタブから戻ってきたときも入れ直される。
  const sendTextRef = useRef(sendText);
  sendTextRef.current = sendText;
  useEffect(() => {
    if (!draft) return;
    const current = sendTextRef.current.trim();
    if (current && current !== draft.text.trim() && !window.confirm('入力中の文章を、ステータス変更時のテンプレートに置き換えますか？')) return;
    setSendText(draft.text);
    setDraftNotice(true);
    requestAnimationFrame(() => {
      resizeTextarea();
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(0, 0);
      if (textareaRef.current) textareaRef.current.scrollTop = 0;
    });
  }, [draft]);

  function clearDraft() {
    setSendText('');
    setDraftNotice(false);
    onDraftUsed?.();
    if (textareaRef.current) textareaRef.current.style.height = '32px';
  }

  useEffect(() => {
    const db = getFirestore();
    updateDoc(doc(db, 'chats', chatRoom.id), { 'company.badge': 0 }).catch(() => {});

    const unsubscribeChat = onSnapshot(doc(db, 'chats', chatRoom.id), (snap) => {
      setUnread(Number(snap.data()?.company?.badge ?? 0) > 0);
    });

    const q = query(collection(db, 'chats', chatRoom.id, 'messages'), orderBy('time', 'asc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          send: data.send ?? '',
          text: data.text ?? '',
          time: toDate(data.time),
          visit: parseVisitRequestMessage(data),
          event: parseEventRequestMessage(data),
        };
      }));
    });

    return () => {
      unsubscribe();
      unsubscribeChat();
    };
  }, [chatRoom.id]);

  // タブを開いた時点で company.badge を0(既読)にしているため、後で返信したいときに未読へ戻せるよう、
  // 未読・既読をボタンで切り替える。未読にした場合も、次にこのタブを開いた時点で既読になる。
  async function toggleUnread() {
    try {
      await updateDoc(doc(getFirestore(), 'chats', chatRoom.id), { 'company.badge': unread ? 0 : 1 });
    } catch (e) {
      console.error('未読・既読の切り替えに失敗しました', e);
    }
  }

  async function send() {
    const text = sendText.trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const db = getFirestore();
      await addDoc(collection(db, 'chats', chatRoom.id, 'messages'), {
        send: company.id,
        receive: chatRoom.user.uid,
        text,
        time: new Date(),
        image: null,
      });
      // 一覧は最後のやり取りの日時(chat.lastSentAt)の新しい順に並べるため、送ったら更新する
      await updateDoc(doc(db, 'chats', chatRoom.id), {
        'chat.lastMessage': text,
        'chat.lastSentAt': new Date(),
        'user.badge': increment(1),
      });

      setSendText('');
      if (textareaRef.current) textareaRef.current.style.height = '32px';
      if (draftNotice) {
        setDraftNotice(false);
        onDraftUsed?.();
      }
    } catch (e) {
      console.error('メッセージを送信できませんでした', e);
      window.alert('メッセージを送信できませんでした。通信状況を確認して、もう一度お試しください。');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  return (
    <div className={Styles.messageThread}>
      <div className={Styles.messageToolbar}>
        <button type="button" className={Styles.messageUnreadBtn} onClick={toggleUnread}>
          {unread ? '既読にする' : '未読にする'}
        </button>
      </div>
      <div className={Styles.messageList}>
        {messages.length === 0 ? (
          <p className={Styles.empty}>まだメッセージがありません。</p>
        ) : (
          messages.map((m, i) => {
            const sent = m.send === company.id;
            const date = (
              <p className={Styles.messageDate}>
                {m.time.getFullYear()}/{m.time.getMonth() + 1}/{m.time.getDate()} {String(m.time.getHours()).padStart(2, '0')}:{String(m.time.getMinutes()).padStart(2, '0')}
              </p>
            );
            // 見学の申込カードは吹き出しに入れず、カードだけを出す
            const bubble = m.visit ? (
              <div className={Styles.messageVisitCard}>
                <VisitRequestCard visit={m.visit} />
                {date}
              </div>
            ) : m.event ? (
              <div className={Styles.messageVisitCard}>
                <EventRequestCard event={m.event} />
                {date}
              </div>
            ) : (
              <div className={sent ? Styles.messageBubbleSent : Styles.messageBubbleReceived}>
                <p className={Styles.messageText}>{m.text}</p>
                {date}
              </div>
            );
            if (sent) return <React.Fragment key={m.id}>{bubble}</React.Fragment>;
            // LINEのように、相手(学生)のメッセージには左にアイコンを出す。
            // 続けて届いたメッセージは最初の1つにだけアイコンと名前を出し、2つ目以降は位置だけそろえる。
            const firstOfGroup = i === 0 || messages[i - 1].send === company.id;
            return (
              <div key={m.id} className={Styles.messageRowReceived}>
                {firstOfGroup ? (
                  <img
                    className={Styles.messageAvatar}
                    src={avatarUrl}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DefaultImg;
                    }}
                  />
                ) : (
                  <span className={Styles.messageAvatarSpacer} />
                )}
                <div className={Styles.messageReceivedBody}>
                  {firstOfGroup && <p className={Styles.messageSenderName}>{chatRoom.user.name}</p>}
                  {bubble}
                </div>
              </div>
            );
          })
        )}
      </div>

      {draftNotice && (
        <div className={Styles.messageDraftNotice}>
          <span>ステータス変更時のテンプレートを入れました。内容を確認して送信してください。</span>
          <button type="button" onClick={clearDraft}>取り消す</button>
        </div>
      )}
      <div className={Styles.messageInputRow}>
        <textarea
          ref={textareaRef}
          className={Styles.messageInput}
          placeholder="メッセージを入力"
          value={sendText}
          onChange={(e) => {
            setSendText(e.target.value);
            if (!e.target.value.trim()) setDraftNotice(false);
            resizeTextarea();
          }}
        />
        <button type="button" className={Styles.messageSendBtn} onClick={send} disabled={sending}>
          {sending ? '送信中…' : '送信'}
        </button>
      </div>
    </div>
  );
};

export default MessageTab;
